import dayjs from 'dayjs';

import {
  AVG_HEART_RATE,
  BLOOD_GLUCOSE,
  BLOOD_PRESSURE,
  BODY_FAT_PERCENTAGE,
  BODY_HEIGHT,
  BODY_MASS_INDEX,
  BODY_TEMPERATURE,
  BODY_WEIGHT,
  CHEST_CM,
  DISTANCE,
  ENERGY_BURNED,
  getObservationConfig,
  HEART_RATE,
  HIPS_CM,
  HR_VARIABILITY,
  MUSCLE_MASS,
  OXYGEN_SATURATION,
  PAIN,
  RESPIRATION,
  RESTING_HEART_RATE,
  SLEEP_DURATION,
  SLEEP_SCORE,
  STEPS,
  STRESS_INDEX,
  THIGH_CM,
  WAIST_CM,
  WAIST_HEIGHT_RATIO,
  WAIST_HIP_RATIO,
} from '@constants/fhir';

import { Interval } from '@models/graph';
import type { ObservationRecord } from '@models/observation';
import { TrackerType } from '@models/vital-sign';

import { calculateMinDate } from '@utils/date';

import { useLazyGetLatestObservations } from './api/observation';

export function useLazyGetMetricDateRange(category: TrackerType, code: string) {
  const {
    query,
    response: { data, loading },
  } = useLazyGetLatestObservations();
  const response = getMetricDateRange(data, category, code);

  async function fetch(patient: string) {
    const { data, loading } = await query(patient, LATEST_OBS_INPUT);
    const response = getMetricDateRange(data, category, code);
    return { ...response, loading };
  }

  return { query: fetch, response: { ...response, loading } };
}

function getMetricDateRange(
  data: Record<string, ObservationRecord | null>,
  category: TrackerType,
  code: string
) {
  const {
    // Body
    blood_sugar,
    body_height,
    body_weight,
    body_mass_index,
    muscle_mass,
    body_fat_percentage,
    body_chest_cm,
    body_waist_cm,
    body_hips_cm,
    body_thigh_cm,
    body_waist_hip,
    body_waist_height,
    // Vital sign
    heart_rate,
    temperature,
    blood_pressure,
    respiration,
    oxygen_saturation,
    pain,
    hrv,
    stress_index,
    // Fitness
    distance,
    steps,
    fitness_average_heart_rate,
    resting_heart_rate,
    energy_burned,
    // Sleep
    sleep_average_heart_rate,
    sleep_duration,
    sleep_score,
  } = data;
  let dateTo: string | null | undefined;

  const interval =
    getObservationConfig(code)?.graph?.graphInterval ?? Interval.Week;
  if (category === TrackerType.VitalSigns) {
    switch (code) {
      case BLOOD_PRESSURE:
        dateTo = blood_pressure?.testDate;
        break;
      case HEART_RATE:
        dateTo = heart_rate?.testDate;
        break;
      case OXYGEN_SATURATION:
        dateTo = oxygen_saturation?.testDate;
        break;
      case PAIN:
        dateTo = pain?.testDate;
        break;
      case RESPIRATION:
        dateTo = respiration?.testDate;
        break;
      case BODY_TEMPERATURE:
        dateTo = temperature?.testDate;
        break;
      case HR_VARIABILITY:
        dateTo = hrv?.testDate;
        break;
      case STRESS_INDEX:
        dateTo = stress_index?.testDate;
        break;
      default:
        break;
    }
  } else if (category === TrackerType.Body) {
    switch (code) {
      case BLOOD_GLUCOSE:
        dateTo = blood_sugar?.testDate;
        break;
      case BODY_HEIGHT:
        dateTo = body_height?.testDate;
        break;
      case BODY_MASS_INDEX:
        dateTo = body_mass_index?.testDate;
        break;
      case BODY_WEIGHT:
        dateTo = body_weight?.testDate;
        break;
      case MUSCLE_MASS:
        dateTo = muscle_mass?.testDate;
        break;
      case BODY_FAT_PERCENTAGE:
        dateTo = body_fat_percentage?.testDate;
        break;
      case HIPS_CM:
        dateTo = body_hips_cm?.testDate;
        break;
      case WAIST_CM:
        dateTo = body_waist_cm?.testDate;
        break;
      case CHEST_CM:
        dateTo = body_chest_cm?.testDate;
        break;
      case THIGH_CM:
        dateTo = body_thigh_cm?.testDate;
        break;
      case WAIST_HEIGHT_RATIO:
        dateTo = body_waist_height?.testDate;
        break;
      case WAIST_HIP_RATIO:
        dateTo = body_waist_hip?.testDate;
        break;
      default:
        break;
    }
  } else if (category === TrackerType.Fitness) {
    switch (code) {
      case ENERGY_BURNED:
        dateTo = energy_burned?.testDate;
        break;
      case DISTANCE:
        dateTo = distance?.testDate;
        break;
      case AVG_HEART_RATE:
        dateTo = fitness_average_heart_rate?.testDate;
        break;
      case RESTING_HEART_RATE:
        dateTo = resting_heart_rate?.testDate;
        break;
      case STEPS:
        dateTo = steps?.testDate;
        break;
      default:
        break;
    }
  } else {
    switch (code) {
      case AVG_HEART_RATE:
        dateTo = sleep_average_heart_rate?.testDate;
        break;
      case SLEEP_DURATION:
        dateTo = sleep_duration?.testDate;
        break;
      case SLEEP_SCORE:
        dateTo = sleep_score?.testDate;
        break;
      default:
        break;
    }
  }
  const maxDate = dayjs(dateTo ?? new Date())
    .endOf('day')
    .toISOString();
  const minDate = calculateMinDate(maxDate, interval);

  return {
    interval,
    minDate: new Date(minDate),
    maxDate: new Date(maxDate),
  };
}

const LATEST_OBS_INPUT = {
  // Body
  blood_sugar: true,
  body_height: true,
  body_weight: true,
  body_mass_index: true,
  muscle_mass: true,
  body_fat_percentage: true,
  body_chest_cm: true,
  body_waist_cm: true,
  body_hips_cm: true,
  body_thigh_cm: true,
  body_waist_hip: true,
  body_waist_height: true,
  // Vital sign
  heart_rate: true,
  temperature: true,
  blood_pressure: true,
  respiration: true,
  oxygen_saturation: true,
  pain: true,
  hrv: true,
  // stress_index: true,
  // Fitness
  distance: true,
  steps: true,
  fitness_average_heart_rate: true,
  resting_heart_rate: true,
  energy_burned: true,
  // Sleep
  sleep_duration: true,
  sleep_score: true,
  sleep_average_heart_rate: true,
};
